/**
 * @file Vibrations : Capacitor Haptics dans l'application native (moteur haptique du telephone, plus fin qu'une vibration brute),
 * repli `navigator.vibrate` dans le navigateur / la PWA (Android Chrome ; iOS Safari l'ignore). Le plugin est charge a la demande.
 */
import type * as HapticsModule from '@capacitor/haptics';

/** Intensites abstraites utilisees par le jeu (traduites en ImpactStyle / NotificationType ou en duree de vibration web). */
export type HapticKind = 'tick' | 'light' | 'medium' | 'heavy' | 'success' | 'warning';

/** Duree (ms) ou motif de repli `navigator.vibrate` pour chaque intensite. */
const WEB_PATTERN: Readonly<Record<HapticKind, number | number[]>> = {
  tick: 8,
  light: 12,
  medium: 22,
  heavy: 40,
  success: [16, 60, 26],
  warning: [30, 50, 30],
};

export interface HapticsSink {
  play(kind: HapticKind): void;
}

type HapticsPlugin = typeof HapticsModule;

const isNative = (): boolean => {
  const cap = (globalThis as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
};

export class DeviceHaptics implements HapticsSink {
  /** Coupe par le reglage « Vibrations ». */
  enabled = true;
  private plugin: Promise<HapticsPlugin | null> | null = null;

  play(kind: HapticKind): void {
    if (!this.enabled) return;
    if (isNative()) {
      this.plugin ??= import('@capacitor/haptics').catch(() => null);
      void this.plugin.then((m) => (m ? DeviceHaptics.native(m, kind) : DeviceHaptics.web(kind))).catch(() => undefined);
      return;
    }
    DeviceHaptics.web(kind);
  }

  private static async native(m: HapticsPlugin, kind: HapticKind): Promise<void> {
    const { Haptics, ImpactStyle, NotificationType } = m;
    switch (kind) {
      case 'tick':
        return Haptics.selectionChanged();
      case 'light':
        return Haptics.impact({ style: ImpactStyle.Light });
      case 'medium':
        return Haptics.impact({ style: ImpactStyle.Medium });
      case 'heavy':
        return Haptics.impact({ style: ImpactStyle.Heavy });
      case 'success':
        return Haptics.notification({ type: NotificationType.Success });
      case 'warning':
        return Haptics.notification({ type: NotificationType.Warning });
    }
  }

  private static web(kind: HapticKind): void {
    try {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(WEB_PATTERN[kind]);
    } catch {
      // vibrate peut lever hors geste utilisateur sur certains navigateurs : sans consequence
    }
  }
}
